// src/components/VoiceCall.js
// Platinum only — free voice calls via Daily.co (WebRTC, no cost for basic usage)
// Setup: create a free account at daily.co, get your domain, put it in .env
import { useState, useEffect, useRef } from 'react'
import './VoiceCall.css'

const DAILY_DOMAIN = process.env.REACT_APP_DAILY_DOMAIN // e.g. https://yourname.daily.co

export default function VoiceCall({ profile }) {
  const [callState, setCallState] = useState('idle') // idle | connecting | active | ended
  const [callFrame, setCallFrame] = useState(null)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState('')
  const containerRef = useRef(null)
  const timerRef = useRef(null)

  // Room name is unique per user so Sky Bri joins the same room
  const roomName = `skybri-platinum-${profile?.id?.slice(0, 8) || 'member'}`
  const roomUrl = DAILY_DOMAIN ? `${DAILY_DOMAIN}/${roomName}` : null

  useEffect(() => {
    // Load Daily.co script
    if (!window.DailyIframe) {
      const script = document.createElement('script')
      script.src = 'https://unpkg.com/@daily-co/daily-js'
      script.async = true
      document.body.appendChild(script)
    }
    return () => {
      if (callFrame) callFrame.destroy()
      clearInterval(timerRef.current)
    }
  }, [])

  async function startCall() {
    if (!roomUrl) {
      setError('Voice calls are not configured yet. Add REACT_APP_DAILY_DOMAIN to your .env file.')
      return
    }

    setCallState('connecting')
    setError('')

    try {
      // Create room via Daily.co REST API (you need to call this from your backend/Supabase edge function)
      // For now, we assume the room exists or auto-creates. See guide for backend setup.
      const frame = window.DailyIframe.createFrame(containerRef.current, {
        iframeStyle: {
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '100%',
          border: 'none', borderRadius: '0',
          background: '#0a0807',
        },
        showLeaveButton: true,
        showFullscreenButton: false,
      })

      frame.on('joining-meeting', () => setCallState('connecting'))
      frame.on('joined-meeting', () => {
        setCallState('active')
        timerRef.current = setInterval(() => setDuration(d => d + 1), 1000)
      })
      frame.on('left-meeting', () => {
        setCallState('ended')
        clearInterval(timerRef.current)
      })
      frame.on('error', (e) => {
        setError('Call error: ' + (e?.errorMsg || 'Unknown error'))
        setCallState('idle')
      })

      await frame.join({ url: roomUrl, audioSource: true, videoSource: false })
      setCallFrame(frame)
    } catch (e) {
      setError('Could not start call. Check your Daily.co configuration.')
      setCallState('idle')
    }
  }

  async function endCall() {
    if (callFrame) {
      await callFrame.leave()
      callFrame.destroy()
      setCallFrame(null)
    }
    clearInterval(timerRef.current)
    setCallState('ended')
    setDuration(0)
  }

  function formatDuration(s) {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  return (
    <div className="voice-call">
      <div className="voice-call__header">
        <div className="voice-call__header-top">
          <span className="voice-call__eyebrow">Platinum Feature</span>
          <span className="badge badge--platinum">Voice Call</span>
        </div>
        <h2 className="voice-call__title">Talk to Sky Bri</h2>
        <p className="voice-call__desc">
          Your Platinum card includes private voice calls. Sky Bri joins the same room when she's available.
          Calls are audio-only and end-to-end encrypted.
        </p>
      </div>

      {error && <div className="voice-call__error">{error}</div>}

      {callState === 'idle' && (
        <div className="voice-call__idle">
          <div className="voice-call__avatar">S</div>
          <p className="voice-call__ready">Ready to call Sky Bri</p>
          <p className="voice-call__note">
            Your private room is reserved. She will receive a notification when you join.
          </p>
          <button className="btn btn--plat" onClick={startCall}>
            Start Voice Call
          </button>
        </div>
      )}

      {callState === 'connecting' && (
        <div className="voice-call__connecting">
          <div className="voice-call__pulse" />
          <p>Connecting...</p>
        </div>
      )}

      {callState === 'active' && (
        <div className="voice-call__active">
          <div className="voice-call__active-indicator">
            <div className="voice-call__wave" />
            <div className="voice-call__wave voice-call__wave--2" />
            <div className="voice-call__wave voice-call__wave--3" />
          </div>
          <p className="voice-call__duration">{formatDuration(duration)}</p>
          <p className="voice-call__active-label">Call in progress</p>
          <button className="btn btn--danger btn--sm" onClick={endCall}>End Call</button>
        </div>
      )}

      {callState === 'ended' && (
        <div className="voice-call__idle">
          <p className="voice-call__ready">Call ended · {formatDuration(duration)}</p>
          <button className="btn btn--plat btn--sm" onClick={() => { setCallState('idle'); setDuration(0) }}>
            Call Again
          </button>
        </div>
      )}

      {/* Daily.co iframe mounts here when active */}
      <div
        ref={containerRef}
        className="voice-call__frame"
        style={{ display: callState === 'active' || callState === 'connecting' ? 'block' : 'none' }}
      />
    </div>
  )
}
